import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useAuthenticatedFetch } from '../useAuth';
import { ExportJobRecord, ExportFormatSchema, type ExportQuality, type ExportReportType, type ExportViewRule } from '../../domains/export/models/ExportRequest';
import { parseApiErrorBody } from '../../domains/shared/utils/planUpgradeError';
import type { OrgChartViewpoint } from '../../stores/orgchartViewStore';

// Define ExportFormat type from the schema
export type ExportFormat = z.infer<typeof ExportFormatSchema>;
export type ExportQualityOption = ExportQuality;

interface UseExportJobProps {
  diagramId: string | undefined;
  enabled?: boolean;
  selectedShapeIds?: string[];
}

export interface CreateExportParams {
  format: ExportFormat;
  projectionContext?: ExportJobRecord['projectionContext'];
  quality?: ExportQualityOption;
  reportType?: ExportReportType;
  reportVariant?: string;
  asOfDate?: string;
  activeViewpoint?: OrgChartViewpoint;
  collapsedNodeIds?: string[];
  autoLayoutEnabled?: boolean;
  /** The viewer's lens, for charts whose rules never live in the document. */
  viewRules?: ExportViewRule[];
  progressSince?: string;
  includeBranding?: boolean;
  showRelationshipCaptions?: boolean;
  /** Pass-through for genogram connector captions (matches canvas i18n). */
  locale?: string;
  /** Explicit genogram export viewpoint. */
  exportViewpoint?: 'general' | 'cultural' | 'health';
  /** Optional health viewpoint focus condition for legend parity. */
  healthFocusCondition?: string;
  /** Optional health viewpoint focus index person id for legend parity. */
  healthFocusIndexPersonId?: string;
  /** Current canvas zoom used only for raster non-scaling stroke parity. */
  canvasStrokeScale?: number;
}

const REPORT_TYPES: ExportReportType[] = [
  'genogram.clinical_case',
  'genogram.health_risk_summary',
  'genogram.legal_forensic',
  'genogram.relationship_dynamics',
  'genogram.genealogical_historical',
  'genogram.custom_analytical',
  'genogram.session_progress_snapshot',
  'genogram.anonymized_teaching',
];

function normalizeReportType(value?: string | null): ExportReportType | undefined {
  if (!value) return undefined;
  return (REPORT_TYPES as string[]).includes(value) ? (value as ExportReportType) : undefined;
}

/**
 * Core export job hook. Responsible for kicking off server-side exports,
 * polling job status, and downloading the resulting artifact.
 */
export function useExportJob({
  diagramId,
  enabled = true,
  selectedShapeIds,
}: UseExportJobProps) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [currentDiagramId, setCurrentDiagramId] = useState<string | null>(null);
  const [optimisticJob, setOptimisticJob] = useState<ExportJobRecord | null>(null);

  const createExportMutation = useMutation<ExportJobRecord, Error, CreateExportParams>({
    mutationFn: async ({
      format,
      projectionContext,
      quality,
      reportType,
      reportVariant,
      asOfDate,
      activeViewpoint,
      collapsedNodeIds,
      autoLayoutEnabled,
      viewRules,
      progressSince,
      includeBranding,
      showRelationshipCaptions,
      locale,
      exportViewpoint,
      healthFocusCondition,
      healthFocusIndexPersonId,
      canvasStrokeScale,
    }) => {
      if (!diagramId) throw new Error('Diagram ID is required to create an export.');
      const resp = await authenticatedFetch(`/api/diagrams/${diagramId}/export`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          diagramId,
          format,
          ...(projectionContext ? { projectionContext } : {}),
          ...(quality ? { quality } : {}),
          ...(reportType ? { reportType } : {}),
          ...(reportVariant ? { reportVariant } : {}),
          ...(selectedShapeIds && selectedShapeIds.length > 0 && { selectedShapeIds }),
          ...(asOfDate ? { asOfDate } : {}),
          ...(activeViewpoint ? { activeViewpoint } : {}),
          ...(collapsedNodeIds && collapsedNodeIds.length > 0 && { collapsedNodeIds }),
          ...(typeof autoLayoutEnabled === 'boolean' && { autoLayoutEnabled }),
          ...(viewRules && viewRules.length > 0 && { viewRules }),
          ...(progressSince ? { progressSince } : {}),
          ...(typeof includeBranding === 'boolean' && { includeBranding }),
          ...(typeof showRelationshipCaptions === 'boolean' && { showRelationshipCaptions }),
          ...(locale && locale.trim().length > 0 && { locale: locale.trim() }),
          ...(exportViewpoint ? { exportViewpoint } : {}),
          ...(healthFocusCondition ? { healthFocusCondition } : {}),
          ...(healthFocusIndexPersonId ? { healthFocusIndexPersonId } : {}),
          ...(typeof canvasStrokeScale === 'number' ? { canvasStrokeScale } : {}),
        }),
      });
      if (!resp.ok) {
        const errorBody = await resp.json().catch(() => ({}));
        throw parseApiErrorBody(errorBody, 'Failed to start export');
      }
      const response = await resp.json() as { jobId: string; status: string; requestId: string };
      setJobId(response.jobId);
      setCurrentDiagramId(diagramId);
      // Create a minimal job record for the initial response
      const job: ExportJobRecord = {
        id: response.jobId,
        diagramId: diagramId,
        format: format,
        status: response.status as any,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
        requestId: response.requestId,
        ...(quality ? { quality } : {}),
        ...(reportType ? { reportType } : {}),
        ...(reportVariant ? { reportVariant } : {}),
        ...(progressSince ? { progressSince } : {}),
        ...(typeof canvasStrokeScale === 'number' ? { canvasStrokeScale } : {}),
      };
      setOptimisticJob(job);
      return job;
    },
    onSuccess: (job) => {
      setOptimisticJob(job);
      queryClient.setQueryData(['exports', 'job', job.id], job);
      queryClient.invalidateQueries({ queryKey: ['exports', 'job', job.id] });
    },
    onError: (error) => {
      console.error('Error creating export:', error);
      setJobId(null); // Clear job ID on failure to allow retrying
      setOptimisticJob(null);
    },
  });

  const { data: job, isLoading: isJobStatusLoading, error: jobStatusError } = useQuery<ExportJobRecord, Error>({
    queryKey: ['exports', 'job', jobId],
    queryFn: async () => {
      if (!jobId) throw new Error('Job ID is required to fetch status.');
      // Timeout prevents the fetch from hanging indefinitely when the worker
      // event loop is blocked (e.g. WASM rasterization in wrangler dev).
      // Status is a KV-only read — anything over 5s means the server is blocked.
      const resp = await authenticatedFetch(`/api/exports/${jobId}/status`, {
        signal: AbortSignal.timeout(5_000),
      });
      if (!resp.ok) {
        const errorBody = await resp.json().catch(() => ({}));
        throw parseApiErrorBody(errorBody, 'Failed to fetch export status');
      }
      const response = await resp.json() as {
        id: string;
        status: string;
        format: string;
        createdAt: string;
        updatedAt: string;
        errorCode?: string;
        errorMessage?: string;
        reportType?: string;
        reportVariant?: string;
      };
      // Convert the response to ExportJobRecord format
      const parsedReportType = normalizeReportType(response.reportType);
      const job: ExportJobRecord = {
        id: response.id,
        diagramId: currentDiagramId || diagramId || '', // Use stored diagramId or fallback to current
        format: response.format as any,
        status: response.status as any,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
        errorCode: response.errorCode,
        errorMessage: response.errorMessage,
        ...(parsedReportType ? { reportType: parsedReportType } : {}),
        ...(response.reportVariant ? { reportVariant: response.reportVariant } : {}),
      };
      return job;
    },
    enabled: Boolean(jobId && enabled),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'pending' || status === 'processing') {
        return 1000;
      }
      // Keep polling if there's an error but no terminal status yet
      // (handles transient failures during server-side rendering)
      if (query.state.error && !status) {
        return 2000;
      }
      return false;
    },
    retry: 3,
    retryDelay: 1000,
  });

  const downloadArtifactMutation = useMutation<{
    blob: Blob;
    filename?: string;
    contentType?: string | null;
  }, Error, string>({
    mutationFn: async (id: string) => {
      const resp = await authenticatedFetch(`/api/exports/${id}/file`);
      if (!resp.ok) {
        const errorBody = await resp.json().catch(() => ({}));
        throw parseApiErrorBody(errorBody, 'Failed to download artifact');
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition');
      const contentType = resp.headers.get('content-type');
      return {
        blob,
        filename: disposition ? parseFilename(disposition) : undefined,
        contentType,
      };
    },
  });

  return {
    createExport: createExportMutation.mutateAsync,
    downloadArtifact: downloadArtifactMutation.mutateAsync,
    job: job ?? optimisticJob ?? undefined,
    isLoading: createExportMutation.isPending || isJobStatusLoading || downloadArtifactMutation.isPending,
    error: createExportMutation.error || jobStatusError || downloadArtifactMutation.error,
    reset: () => {
      setJobId(null);
      setCurrentDiagramId(null);
      setOptimisticJob(null);
      createExportMutation.reset();
      downloadArtifactMutation.reset();
      queryClient.removeQueries({ queryKey: ['exports', 'job', jobId] });
    },
  };
}

function parseFilename(disposition: string): string | undefined {
  const filenameStarMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return filenameStarMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const filenameMatch = disposition.match(/filename=([^;]+)/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].trim().replace(/^"|"$/g, '');
  }
  return undefined;
}
